/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import { Router, RouterStateSnapshot, ActivatedRouteSnapshot } from '@angular/router';
import jwt_decode from 'jwt-decode';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { ILoggerService } from '@touchplan/logging';
import { ProcoreLoginStateService } from 'src/app/services/procore-login-state.service';
import { AuthService } from '../auth.service';
import { signInWithCustomToken, Auth } from '@angular/fire/auth';
import { browserLocalPersistence, setPersistence } from 'firebase/auth';
import { ICustomAuthToken } from 'src/app/app-data/interfaces/custom-auth-token';

export enum UnsubstantiatedRoutePaths {
	logout = 'logout',
	auth = 'auth',
	procoreResponse = 'procore-response',
	clear = 'clear',
}

@Injectable()
export class UnsubstantiatedRouteExecution {
	private logger: ILoggerService;
	constructor(
		private router: Router,
		private authService: AuthService,
		private afAuth: Auth,
		private procoreState: ProcoreLoginStateService
	) {
		this.logger = LoggerServiceFactory.create('UnsubstantiatedRouteExecution');
	}

	public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
		switch (route.routeConfig.path) {
			case UnsubstantiatedRoutePaths.logout:
				return this.logout();
			case UnsubstantiatedRoutePaths.auth:
				return this.auth(route);
			case UnsubstantiatedRoutePaths.procoreResponse:
				return this.handleProcoreResponse(route);
			case UnsubstantiatedRoutePaths.clear:
				return this.clearLocalStorage();
			default:
				return false;
		}
	}

	public logout() {
		this.authService.logout();
		return false;
	}
	public auth(route: ActivatedRouteSnapshot) {
		const token = route.queryParamMap.get('token');
		//special token auth
		if (token) {
			setPersistence(this.afAuth, browserLocalPersistence)
				.then(() => {
					return signInWithCustomToken(this.afAuth, token);
				})
				.then((data) => {
					this.logger.log('authed', data);
					const deTokenify = jwt_decode<ICustomAuthToken>(token);

					if (deTokenify.claims && deTokenify.claims.projectId) {
						this.router.navigate(['projects', deTokenify.claims.projectId]);
					} else {
						this.router.navigate(['projects']);
					}
				})
				.catch((e) => {
					Logging.warning('invalid token');
					this.logger.log('auth failed', e);
				});
			return false;
		}

		return true;
	}

	private clearLocalStorage() {
		localStorage.clear();
		this.router.navigate(['/']).then((s) => {
			Logging.notice('Local storage cleared');
		});
	}

	private handleProcoreResponse(route: ActivatedRouteSnapshot) {
		const authorizationCode = route.queryParamMap.get('code');
		if (authorizationCode) {
			this.procoreState.completeAuthentication(authorizationCode);
		}
		return false;
	}
}
